import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  HostListener,
  AfterViewInit,
  Input,
  inject,
  input,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { NgClass, NgStyle } from '@angular/common';
import { Subscription } from 'rxjs';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzProgressComponent } from 'ng-zorro-antd/progress';
import { StateMachineService } from '../../services/state-machine.service';
import { FormService } from '../../services/form.service';
import { FormValues, Step } from '@aaa/interface-agentScheduler';

@Component({
  selector: 'ava-progress-bar',
  standalone: true,
  template: `
    <div #container>
      @if (containerWidth && containerWidth < 760) {
        <div #mobileContainer style="display: flex; justify-content: center; padding: 10px">
          @if (showBackLink) {
            <a
              (click)="sms.sendEvent('PREVIOUS'); $event.preventDefault()"
              class="aaa-link-color"
              style="display: flex; justify-content: center"
            >
              <i nz-icon style="padding-left: 5px; display: flex; align-items: center">
                <svg
                  style="width: 15px; height: 15px"
                  class="aaa-link-fill"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 512 512"
                >
                  <title>left arrow</title>
                  <path d="M388.262 511.63h-104.72l-160.036-255.694 160.036-255.565h104.72l-159.14 256.104z"></path>
                </svg>
              </i>
            </a>
          }
          <div [ngStyle]="{ padding: showBackLink ? '0 20px 0 5px' : '0 5px' }">
            {{ title }}
          </div>
        </div>
      }

      @if (containerWidth && containerWidth >= 760) {
        <div #desktopContainer>
          <div style="display: flex">
            @for (step of steps; track step) {
              <div
                style="display: flex; justify-content: center; padding: 20px 0"
                [ngStyle]="{ width: '25%' }"
                [ngClass]="{
                  'aaa-link-color': progressValue >= step.percent,
                  clickable: progressStep > step.step || (step.step === 0 && stateMatches !== 'topic'),
                }"
                (click)="jumpToStep(step, progressValue)"
              >
                {{ step.title }}
                <i nz-icon style="padding-left: 5px; display: flex; align-items: center">
                  <svg
                    class="aaa-link-fill"
                    style="width: 15px; height: 15px"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                  >
                    <title>right arrow</title>
                    <path d="M123.737 511.63h104.72l160.036-255.694-160.036-255.565h-104.72l159.14 256.104z"></path>
                  </svg>
                </i>
              </div>
            }
          </div>

          <nz-progress [nzPercent]="progressValue" [nzShowInfo]="false" nzStrokeColor="#00a0df"></nz-progress>

          <div style="display: flex; justify-content: center">
            <a
              style="font-weight: 500"
              (click)="sms.sendEvent('PREVIOUS'); $event.preventDefault()"
              [ngStyle]="{ opacity: showBackLink ? 1 : 0, 'pointer-events': showBackLink ? 'initial' : 'none' }"
            >
              < Back
            </a>
          </div>
        </div>
      }
    </div>
  `,
  imports: [NgStyle, NgClass, NzIconDirective, NzProgressComponent],
})
export class ProgressBarComponent implements OnInit, OnDestroy, AfterViewInit {
  public sms = inject(StateMachineService);
  public formService = inject(FormService);

  formId = input('');
  formValues: FormValues | undefined;
  @ViewChild('container') container: ElementRef | undefined;
  containerWidth: number | undefined;
  title = '';
  stateMatches = '';
  stepsNormalFlow = [
    {
      title: 'Type of Appointment',
      percent: 25,
      step: 0,
      states: ['topic', 'region', 'meeting'],
      event: 'JUMP_TYPE',
    },
    {
      title: 'Choose an Agent',
      percent: 50,
      step: 1,
      states: ['agentsList'],
      event: 'JUMP_AGENT',
    },
    {
      title: 'Book a Time',
      percent: 75,
      step: 2,
      states: ['agentPage'],
      event: 'JUMP_TIME',
    },
    {
      title: 'Complete Appointment',
      percent: 100,
      step: 3,
      states: ['userInfo', 'confirmation'],
    },
  ];

  stepsAgentFlow = [
    {
      title: 'Choose an Agent',
      percent: 25,
      step: 0,
      states: ['agentsList'],
      event: 'JUMP_AGENT',
    },
    {
      title: 'Book a Time',
      percent: 50,
      step: 1,
      states: ['agentPage'],
      event: 'JUMP_TIME',
    },
    {
      title: 'Type of Appointment',
      percent: 75,
      step: 2,
      states: ['topic', 'region', 'meeting'],
      event: 'JUMP_TYPE',
    },
    {
      title: 'Complete Appointment',
      percent: 100,
      step: 3,
      states: ['userInfo', 'confirmation'],
    },
  ];

  //controller
  steps: Step[] = [];
  stateSub: Subscription | undefined;
  agentFlowSub: Subscription | undefined;

  //template control
  progressValue = 0;
  progressStep = 0;
  showBackLink = false;
  agent: FormControl | undefined;

  ngOnInit(): void {
    if (this.formId()) {
      this.formValues = this.formService.formValues[this.formId()];
      this.agent = this.formService.form[this.formId()]?.get('agent') as FormControl;
    }
    /**
     * In conjunction with this.onPopstate(), we enable moving back in the flow with the browser back button.
     */
    history.pushState(null, document.title, location.href.toLowerCase());

    this.agentFlowSub = this.sms.agentFlowSubject.subscribe((agentFlow: boolean) => {
      this.steps = agentFlow ? this.stepsAgentFlow : this.stepsNormalFlow;
    });

    this.stateSub = this.sms.stateSubject.subscribe((state: any) => {
      if (state && this.steps) {
        if (state.matches('topic')) {
          this.stateMatches = 'topic';
          this.title = 'Book an Appointment';
        }
        if (state.matches('region')) {
          this.stateMatches = 'region';
          this.title = 'Book an Appointment';
        }
        if (state.matches('meeting')) {
          this.stateMatches = 'meeting';
          this.title = 'Book an Appointment';
        }
        if (state.matches('agentsList')) {
          this.stateMatches = 'agentsList';
          this.title = 'Choose a Travel Agent';
        }
        if (state.matches('agentPage')) {
          this.stateMatches = 'agentPage';
          if (this.agent?.value.fName) {
            this.title = 'Book with ' + this.agent.value.fName;
          } else {
            this.title = '';
          }
        }
        if (state.matches('userInfo')) {
          this.stateMatches = 'userInfo';
          this.title = 'Complete Appointment';
        }

        //progress value
        this.steps.forEach((step) => {
          step.states.forEach((stateName) => {
            if (state.matches(stateName)) {
              this.progressValue = step.percent;
              this.progressStep = step.step;
            }
          });
        });

        /**
         * Agent flow:
         * - hide backLink on book time step and agentsList step
         *
         * Topic flow:
         * - show backLink an all steps other than topic
         * - hide backLink on topic step only
         */
        this.showBackLink =
          (!this.formValues?.overrides.agentEmail && !state.matches('topic')) ||
          (this.formValues?.overrides.agentEmail &&
            (state.matches('topic') ||
              state.matches('region') ||
              state.matches('insuranceOptions') ||
              state.matches('meeting') ||
              state.matches('userInfo')));
      }
    });
  }

  ngAfterViewInit(): void {
    this.onResize();
  }

  ngOnDestroy(): void {
    this.agentFlowSub?.unsubscribe();
    this.stateSub?.unsubscribe();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.containerWidth = this.container?.nativeElement.offsetWidth;
  }

  jumpToStep(step: Step, progressValue: number): void {
    if (step.percent > progressValue) return;
    if (step.event) {
      this.sms.sendEvent(step.event);
    }
  }

  @HostListener('window:popstate')
  onPopstate(): void {
    if (this.showBackLink) {
      history.pushState(null, document.title, location.href.toLowerCase());
      this.sms.sendEvent('PREVIOUS');
    }
  }
}
